import { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const ImageUpload = ({ 
  value, 
  onChange, 
  label = "Image",
  accept = "image/*",
  maxSizeMB = 5 
}) => {
  const [uploading, setUploading] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size
    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > maxSizeMB) {
      toast.error(`File too large. Maximum size is ${maxSizeMB}MB`);
      return;
    }

    setUploading(true);

    try {
      // Convert to base64 for preview and storage
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64String = reader.result;
        
        // Upload to backend
        const token = localStorage.getItem('admin_token');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/upload-image`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            filename: file.name,
            file_type: file.type,
            data: base64String
          })
        });

        if (!response.ok) {
          throw new Error('Upload failed');
        }

        const result = await response.json();
        onChange(result.url);
        toast.success('Image uploaded successfully');
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleUrlChange = (e) => {
    onChange(e.target.value);
  };

  const handleRemove = () => {
    onChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-3" data-testid="image-upload">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{label}</span>
        <Button 
          type="button" 
          variant="ghost" 
          size="sm"
          onClick={() => setShowUrlInput(!showUrlInput)}
        >
          {showUrlInput ? 'Upload File' : 'Use URL'}
        </Button>
      </div>

      {showUrlInput ? (
        <Input
          placeholder="Enter image URL..."
          value={value || ''}
          onChange={handleUrlChange}
          data-testid="image-url-input"
        />
      ) : (
        <div className="flex gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            onChange={handleFileUpload}
            className="hidden"
            data-testid="image-file-input"
          />
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Choose File
              </>
            )}
          </Button>
        </div>
      )}

      {/* Preview */}
      {value && (
        <div className="relative rounded-lg overflow-hidden border bg-muted/50">
          <img
            src={value}
            alt="Preview"
            className="w-full h-40 object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div className="hidden w-full h-40 items-center justify-center text-muted-foreground">
            <ImageIcon className="w-8 h-8" />
          </div>
          <Button
            type="button"
            variant="destructive"
            size="icon"
            className="absolute top-2 right-2 w-8 h-8"
            onClick={handleRemove}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      {!value && !showUrlInput && (
        <p className="text-xs text-muted-foreground">
          Supported formats: JPG, PNG, WebP. Max size: {maxSizeMB}MB
        </p>
      )}
    </div>
  );
};

export default ImageUpload;
